import React, { useState, useEffect, useCallback } from 'react';
import { Umbrella, CheckCircle2, AlertCircle, Clock, FileText, Plus, MoreVertical, Loader2, Check, X } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import AbsenceCalendar from './AbsenceCalendar';
import LeaveRequestModal from './LeaveRequestModal';

let globalMockLeaves = [];

const CongesAbsencesTab = ({ employeeId = 'EMP-0007', isAdmin = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      // Mocking fetch delay
      await new Promise(resolve => setTimeout(resolve, 300));
      setLeaveHistory([...globalMockLeaves]);
    } catch (error) {
      console.error("Failed to fetch leaves", error);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const handleApprove = (id) => {
    globalMockLeaves = globalMockLeaves.map(req => req.id === id ? { ...req, status: 'Approuvé' } : req);
    setLeaveHistory([...globalMockLeaves]);
  };

  const handleDecline = (id) => {
    globalMockLeaves = globalMockLeaves.map(req => req.id === id ? { ...req, status: 'Refusé' } : req);
    setLeaveHistory([...globalMockLeaves]);
  };

  useEffect(() => {
    fetchLeaves();
    const handleNewLeave = () => fetchLeaves();
    window.addEventListener('leaveRequestAdded', handleNewLeave);
    return () => window.removeEventListener('leaveRequestAdded', handleNewLeave);
  }, [fetchLeaves]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Approuvé': return 'text-emerald-600 bg-emerald-50 border border-emerald-200';
      case 'Refusé': return 'text-red-600 bg-red-50 border border-red-200';
      case 'En attente': return 'text-orange-600 bg-orange-50 border border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row gap-6">
        
        {/* Left Column (Main Content) */}
        <div className="flex-1 space-y-6">
          
          {/* Top Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            <Card className="border-gray-200 shadow-sm col-span-1">
              <CardContent className="p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Solde de congés <span className="font-normal text-gray-500">(aujourd'hui)</span></h3>
                <div className="grid grid-cols-4 gap-2 text-center mb-4">
                  <div className="flex flex-col items-center">
                    <span className="text-2xl font-bold text-violet-700">0</span>
                    <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1">Jours acquis</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-2xl font-bold text-orange-500">0</span>
                    <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1">Jours pris</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-2xl font-bold text-blue-500">0</span>
                    <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1">En attente</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-2xl font-bold text-emerald-600">0</span>
                    <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1">Jours restants</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mb-2 overflow-hidden flex">
                  <div className="bg-violet-600 h-2" style={{ width: '0%' }}></div>
                  <div className="bg-orange-400 h-2" style={{ width: '0%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 font-medium">
                  <span>0 / 0 jours utilisés</span>
                  <span>0% restants</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-100 shadow-sm bg-orange-50/30 col-span-1">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-sm font-bold text-gray-900">Demandes en attente</h3>
                  <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">{leaveHistory.filter(l => l.status === 'En attente').length}</span>
                </div>
                
                {leaveHistory.filter(l => l.status === 'En attente').map(req => (
                  <div key={req.id} className="bg-white border border-orange-100 rounded-xl p-3 mb-3 relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-400"></div>
                    <p className="font-semibold text-gray-900 text-sm">{req.type}</p>
                    <p className="text-xs text-gray-500 mt-1">Du {req.period}</p>
                    <div className="flex justify-between items-end mt-2 text-xs mb-2">
                      <span className="font-medium text-gray-700">{req.days} jours</span>
                      <span className="text-orange-600 font-semibold bg-orange-50 px-2 py-0.5 rounded border border-orange-100">En attente</span>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
                        <Button variant="outline" size="sm" onClick={() => handleDecline(req.id)} className="flex-1 text-red-600 border-red-200 bg-white hover:bg-red-50 h-7 text-xs">
                          Refuser
                        </Button>
                        <Button size="sm" onClick={() => handleApprove(req.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
                          Approuver
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                
                {leaveHistory.filter(l => l.status === 'En attente').length === 0 && (
                  <p className="text-sm text-gray-500 italic text-center py-4">Aucune demande en attente</p>
                )}
                
                <button className="text-sm font-medium text-violet-600 hover:text-violet-700 text-center w-full">
                  Voir toutes les demandes
                </button>
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-sm col-span-1">
              <CardContent className="p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Conventions</h3>
                <div className="space-y-3 text-xs">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-gray-400 mt-0.5 shrink-0" />
                    <span className="text-gray-700"><span className="font-medium">Acquisition :</span> 1.5 jours / mois travaillé</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock size={14} className="text-gray-400 mt-0.5 shrink-0" />
                    <span className="text-gray-700"><span className="font-medium">Jours max reportables :</span> 10 jours</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-gray-400 mt-0.5 shrink-0" />
                    <span className="text-gray-700"><span className="font-medium">Validation requise :</span> Oui</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText size={14} className="text-gray-400 mt-0.5 shrink-0" />
                    <span className="text-gray-700"><span className="font-medium">Congé maladie :</span> Avec justificatif</span>
                  </div>
                </div>
                <button className="text-sm font-medium text-violet-600 hover:text-violet-700 text-center w-full mt-4">
                  Voir toutes les règles
                </button>
              </CardContent>
            </Card>

          </div>

          {/* Request History Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Historique des demandes</h3>
              {!isAdmin && (
                <Button onClick={() => setIsModalOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm h-9">
                  <Plus size={16} className="mr-2" />
                  Nouvelle demande
                </Button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-100 text-xs uppercase">
                  <tr>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Période</th>
                    <th className="px-6 py-4">Jours</th>
                    <th className="px-6 py-4">Solde après</th>
                    <th className="px-6 py-4">Statut</th>
                    <th className="px-6 py-4">Demandé le</th>
                    <th className="px-6 py-4 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-violet-500" />
                        Chargement de l'historique...
                      </td>
                    </tr>
                  ) : leaveHistory.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        Aucune demande trouvée
                      </td>
                    </tr>
                  ) : (
                    leaveHistory.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{request.type}</td>
                        <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{request.period}</td>
                        <td className="px-6 py-4 font-medium text-gray-900">{request.days}</td>
                        <td className="px-6 py-4 font-medium text-gray-900">{request.balanceAfter}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${getStatusStyle(request.status)}`}>
                            {request.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{request.requestedOn}</td>
                        <td className="px-4 py-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                                <MoreVertical size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40 p-2 rounded-xl shadow-xl border-gray-100">
                              {isAdmin && request.status === 'En attente' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleApprove(request.id)} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-emerald-50 text-emerald-700">
                                    <Check className="mr-2 h-4 w-4" /> Approuver
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDecline(request.id)} className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-red-50 text-red-600">
                                    <X className="mr-2 h-4 w-4" /> Refuser
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuItem className="text-sm font-medium p-2 cursor-pointer rounded-lg hover:bg-gray-50 text-gray-700">
                                Voir les détails
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
        </div>

        {/* Right Column (Sidebar) */}
        <div className="w-full lg:w-[320px] shrink-0">
          <AbsenceCalendar />
          
          {isAdmin && (
            <div className="mt-6 bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h4 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wider">Validation Manager</h4>
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                      <Umbrella size={16} />
                    </div>
                    <span className="font-bold text-gray-900 text-sm">Demande de congé</span>
                  </div>
                  <span className="text-[10px] font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full uppercase tracking-wider">En attente</span>
                </div>
                <p className="font-semibold text-gray-900 text-sm">Ahmed Benali</p>
                <p className="text-gray-600 text-sm">Congé annuel</p>
                <div className="flex justify-between items-center text-xs text-gray-500 mt-2 mb-4">
                  <span>Du 14/09/2026 au 17/09/2026</span>
                  <span className="font-bold text-gray-900">4 jours</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 text-red-600 border-red-200 bg-white hover:bg-red-50 h-9">
                    Refuser
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-9">
                    Approuver
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        
      </div>
      
      <LeaveRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default CongesAbsencesTab;
